import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { adminApi, getApiErrorMessage } from '../services/adminApi'
import { useAdminAuth } from '../context/AdminAuthContext'
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmAction,
  EmptyState,
  Input,
  PageHeader,
  Spinner,
} from '../components/ui'
import { resolveMainAppUrl } from '../utils/appUrl'

export default function Tenants() {
  const { isSuperAdmin, isRevenda } = useAdminAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [tenants, setTenants] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState(location.state?.message || '')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = isRevenda ? await adminApi.listRevendaTenants() : await adminApi.listTenants()
      setTenants(data.data || [])
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [isRevenda])

  useEffect(() => {
    load()
  }, [load])

  const filtered = tenants.filter((t) => {
    const q = search.toLowerCase()
    return (
      t.tenant_id?.toLowerCase().includes(q) ||
      t.company_name?.toLowerCase().includes(q) ||
      t.cnpj?.toLowerCase().includes(q) ||
      t.admin_email?.toLowerCase().includes(q)
    )
  })

  const handleResetPassword = async (tenantId) => {
    try {
      const { data } = await adminApi.resetPassword(tenantId)
      setMessage(data.message || 'Senha resetada.')
    } catch (err) {
      setError(getApiErrorMessage(err))
    }
  }

  const handleToggle = async (t) => {
    const active = t.status !== 'active'
    try {
      await adminApi.toggleTenant(t.tenant_id, active)
      load()
    } catch (err) {
      setError(getApiErrorMessage(err))
    }
  }

  const handleImpersonate = async (tenantId) => {
    setError('')
    try {
      const { data } = await adminApi.impersonate(tenantId)
      const token = data?.data?.accessToken
      if (!token) {
        setError('Token de acesso não retornado pelo servidor.')
        return
      }
      const appUrl = resolveMainAppUrl()
      const url = `${appUrl}/login?token=${encodeURIComponent(token)}&tenant=${encodeURIComponent(tenantId)}`
      const opened = window.open(url, '_blank', 'noopener,noreferrer')
      if (!opened) {
        window.location.href = url
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível entrar no tenant'))
    }
  }

  const handleDelete = async (tenantId) => {
    try {
      await adminApi.deleteTenant(tenantId)
      setMessage('Cliente removido.')
      load()
    } catch (err) {
      setError(getApiErrorMessage(err))
    }
  }

  if (loading) return <Spinner />

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Operação"
        title={isRevenda ? 'Minhas bases' : 'Clientes'}
        description={`${filtered.length} registro(s) disponível(is) para gerenciamento.`}
        icon="fa-building"
        actions={<Button icon="fa-plus" onClick={() => navigate('/tenants/new')}>Novo cliente</Button>}
      />

      {error && <Alert type="error">{error}</Alert>}
      {message && <Alert type="success">{message}</Alert>}

      <Card bodyClassName="!p-0">
        <div className="border-b border-slate-100 p-4">
          <Input
            placeholder="Buscar por ID, empresa ou e-mail…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="mp-scrollbar overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50/80">
              <tr className="border-b text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-3.5">ID</th>
                <th className="px-5 py-3.5">Empresa</th>
                <th className="px-5 py-3.5">CNPJ</th>
                <th className="px-5 py-3.5">Pessoas</th>
                <th className="px-5 py-3.5">Status</th>
                <th className="px-5 py-3.5">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.tenant_id} className="border-b border-slate-100 transition hover:bg-blue-50/40">
                  <td className="px-5 py-4 font-mono text-xs">{t.tenant_id}</td>
                  <td className="px-5 py-4">
                    <div className="font-semibold text-brand-deep">{t.company_name}</div>
                    <div className="text-xs text-slate-500">{t.admin_email}</div>
                  </td>
                  <td className="px-5 py-4 font-mono text-xs text-slate-600">{t.cnpj || '—'}</td>
                  <td className="px-5 py-4">
                    {t.pessoas_count ?? 0} / {t.max_pessoas ?? '—'}
                  </td>
                  <td className="px-5 py-4">
                    <Badge tone={t.status === 'active' ? 'success' : 'danger'}>
                      {t.status === 'active' ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap gap-1">
                      <Button variant="ghost" className="!px-2 !py-1 text-xs" onClick={() => handleImpersonate(t.tenant_id)}>
                        Entrar
                      </Button>
                      <Button variant="ghost" className="!px-2 !py-1 text-xs" onClick={() => navigate(`/tenants/${encodeURIComponent(t.tenant_id)}/edit`)}>
                        Editar
                      </Button>
                      <ConfirmAction variant="ghost" className="!px-2 !py-1 text-xs" label="Reset senha" confirmLabel="Resetar" onConfirm={() => handleResetPassword(t.tenant_id)} />
                      <Button variant="ghost" className="!px-2 !py-1 text-xs" onClick={() => handleToggle(t)}>
                        {t.status === 'active' ? 'Desativar' : 'Ativar'}
                      </Button>
                      {isSuperAdmin && (
                        <ConfirmAction className="!px-2 !py-1 text-xs" onConfirm={() => handleDelete(t.tenant_id)} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <EmptyState title="Nenhum cliente encontrado" description="Ajuste a busca ou cadastre um novo cliente." icon="fa-building" />}
        </div>
      </Card>
    </div>
  )
}
